import { useState, useRef } from "react";
import type { FactoryProfile } from "@/mocks/factoryProfiles";

interface Message {
  id: number;
  from: "buyer" | "seller";
  text: string;
  time: string;
}

interface Props {
  factory: FactoryProfile;
  onClose: () => void;
}

const sellerReplies = [
  "Thank you for your interest! We'd be happy to provide a detailed quotation for your requirements.",
  "Our minimum order quantity is flexible for sample orders. Please share your specifications.",
  "We can provide full certification documentation including ISO, CE, and test reports.",
  "Our production lead time is typically 15–30 days after order confirmation.",
  "We offer OEM/ODM services with custom branding and packaging.",
  "We ship worldwide via sea freight, air freight, and express courier.",
  "Our quality control team inspects every batch before shipment.",
  "We accept T/T, L/C, PayPal, and Western Union payment methods.",
  "We can arrange a factory video tour or third-party inspection if needed.",
  "Volume discounts are available for orders above 50 units.",
];

export default function FactoryChatModal({ factory, onClose }: Props) {
  const [nameSet, setNameSet] = useState(false);
  const [buyerName, setBuyerName] = useState("");
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      from: "seller",
      text: `Hello! Welcome to ${factory.name}. We are a ${factory.certifications.slice(0, 3).join(", ")} certified manufacturer with ${2026 - factory.founded} years of experience. How can I assist you today?`,
      time: "Now",
    },
    {
      id: 2,
      from: "seller",
      text: `We specialize in ${factory.specialties.join(", ")}. Our production capacity is ${factory.productionCapacity}. Feel free to ask about pricing, MOQ, or customization!`,
      time: "Now",
    },
  ]);
  const bottomRef = useRef<HTMLDivElement>(null);

  const sendMessage = () => {
    if (!input.trim()) return;
    const now = new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
    setMessages(prev => [...prev, { id: Date.now(), from: "buyer", text: input.trim(), time: now }]);
    setInput("");
    setTimeout(() => {
      const reply = sellerReplies[Math.floor(Math.random() * sellerReplies.length)];
      setMessages(prev => [...prev, { id: Date.now() + 1, from: "seller", text: reply, time: now }]);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    }, 900 + Math.random() * 700);
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  };

  if (!nameSet) {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-2xl w-full max-w-sm p-8 text-center" onClick={e => e.stopPropagation()}>
          <div className="w-16 h-16 rounded-xl overflow-hidden mx-auto mb-4">
            <img src={factory.image} alt={factory.name} className="w-full h-full object-cover object-top" />
          </div>
          <h3 className="font-extrabold text-gray-900 text-lg mb-1">Chat with Factory</h3>
          <p className="text-gray-500 text-sm mb-1">{factory.flag} {factory.name}</p>
          <p className="text-gray-400 text-xs mb-5">Replies {factory.responseTime} · {factory.certifications.slice(0, 2).join(", ")} Certified</p>
          <input
            value={buyerName}
            onChange={e => setBuyerName(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && buyerName.trim()) setNameSet(true); }}
            placeholder="Your name"
            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors mb-3"
            autoFocus
          />
          <button
            onClick={() => { if (buyerName.trim()) setNameSet(true); }}
            className="w-full bg-amber-400 hover:bg-amber-500 text-white font-bold py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
          >
            Start Chat
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md flex flex-col" style={{ height: 580 }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 shrink-0">
          <div className="w-10 h-10 rounded-xl overflow-hidden shrink-0">
            <img src={factory.image} alt={factory.name} className="w-full h-full object-cover object-top" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-gray-900 text-sm truncate">{factory.flag} {factory.name}</p>
            <p className="text-xs text-gray-400 truncate">{factory.city} · {factory.certifications.slice(0, 2).join(", ")}</p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="w-2 h-2 bg-green-400 rounded-full"></span>
            <span className="text-xs text-green-600 font-semibold">Online</span>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer shrink-0">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Factory info strip */}
        <div className="px-5 py-2.5 bg-amber-50 border-b border-amber-100 shrink-0 flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <i className="ri-shield-check-line text-green-500 text-xs"></i>
            <span className="text-xs text-green-700 font-semibold">Verified</span>
          </div>
          <span className="text-gray-300">|</span>
          <span className="text-xs text-amber-700 font-semibold">{factory.productionCapacity}</span>
          <span className="text-gray-300">|</span>
          <span className="text-xs text-gray-500">Replies {factory.responseTime}</span>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-3">
          {messages.map(msg => (
            <div key={msg.id} className={`flex ${msg.from === "buyer" ? "justify-end" : "justify-start"}`}>
              {msg.from === "seller" && (
                <div className="w-7 h-7 rounded-full overflow-hidden shrink-0 mr-2 mt-0.5">
                  <img src={factory.image} alt="" className="w-full h-full object-cover object-top" />
                </div>
              )}
              <div className={`max-w-[75%] flex flex-col gap-0.5 ${msg.from === "buyer" ? "items-end" : "items-start"}`}>
                <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${msg.from === "buyer" ? "bg-amber-400 text-white rounded-br-sm" : "bg-gray-100 text-gray-800 rounded-bl-sm"}`}>
                  {msg.text}
                </div>
                <span className="text-xs text-gray-400 px-1">{msg.time}</span>
              </div>
              {msg.from === "buyer" && (
                <div className="w-7 h-7 flex items-center justify-center bg-amber-400 rounded-full shrink-0 ml-2 mt-0.5">
                  <span className="text-xs text-white font-bold">{buyerName.charAt(0).toUpperCase()}</span>
                </div>
              )}
            </div>
          ))}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div className="px-5 py-4 border-t border-gray-100 shrink-0">
          <div className="flex gap-2">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              placeholder="Type a message..."
              className="flex-1 border border-gray-200 rounded-full px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim()}
              className="w-10 h-10 flex items-center justify-center bg-amber-400 hover:bg-amber-500 disabled:bg-gray-200 text-white rounded-full transition-colors cursor-pointer shrink-0"
            >
              <i className="ri-send-plane-fill text-sm"></i>
            </button>
          </div>
          <p className="text-xs text-gray-400 text-center mt-2">Press Enter to send · Typically replies {factory.responseTime}</p>
        </div>
      </div>
    </div>
  );
}
